import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { getSelectedBank } from 'src/redux/modules/bankSelection/bankSelection.selectors';
import { getMarketVolatility } from 'src/redux/modules/marketVolatility/marketVolatility.selectors';
import {
  IRA_WITHHOLDING_WARNING_MODAL,
  openModal,
} from 'src/redux/modules/modals/modals.module';
import { getHasAPP } from 'src/redux/modules/productTypes/productType.selectors';
import { setProgressFlag } from 'src/redux/modules/progress';
import {
  getProgressCurrentStep,
  getSellWithholdingWarned,
} from 'src/redux/modules/progress/selectors';
import { setShowAllFundsStep } from 'src/redux/modules/quickBuy/quickBuy.module';
import { setShowAllSellFunds } from 'src/redux/modules/quickSell/quickSell.module';
import { getDeliveryMethod } from 'src/redux/modules/sellDeliveryMethod/sellDeliveryMethod.selectors';
import {
  deselectSellFund,
  selectSellFund,
  updateSellFund,
  updateSellFundAmount,
  updateSellFundDistributionDisclaimer,
  updateSellFundDistributionPayee,
  updateSellFundDistributionReason,
  updateSellFundUnit,
} from 'src/redux/modules/sellTransaction/sellTransaction.module';
import {
  getHasNotInheritedSEPOrSimpleIRA,
  getIsQuickSellWithoutZeroBalance,
  getQuickSellAgreementID,
  getTransactionLimit,
} from 'src/redux/modules/sellTransaction/sellTransaction.selectors';
import { getCollegeSellFundsList } from 'src/redux/modules/shoppingCart/collegeShoppingCart.selectors';
import {
  getHiddenAccountNumber,
  getUserFundList,
} from 'src/redux/modules/shoppingCart/shoppingCart.selectors';
import {
  getPersonalInformationData,
  getUserAddress,
} from 'src/redux/modules/user/PersonalInformation/personalInformation.selectors';
import { isRetirementFund } from 'src/utils/funds/funds-utility';
import { strictMemoize } from 'src/utils/utils';
import ExpandedFundList from '../../Buy/BuyFlow/ExpandedFundList/ExpandedFundList';
import CollegeContributeFundGroupHeader from '../../CollegeContribute/CollegeContributeFundGroupHeader/CollegeContributeFundGroupHeader';
import StepExpanded from '../../Shared/StepExpanded/StepExpanded';
import CollegeDistributeFundEditor from '../CollegeDistributeFundEditor/CollegeDistributeFundEditor';
import './CollegeDistributeFundList.scss';

/**
 * Given the actions object, it creates a custom selectFund action creator that calls the underlying
 * selectSellFund action creator and also opens the IRA withholding modal if appropriate.
 *
 * To implement this functionality we need a combination of data: the fund that is being selected, the progress
 * flag for if we've already warned the user, and we need the open modal action.
 *
 * If we try to put this code in the selectSellFund action creator directly, we run into issues of not having
 * access to the flags or open modal action creator. We can't effectively use a class method as is used for most
 * callbacks because here we need to embed our method in the actions object that is passed downstream.
 *
 * The memoize is used to avoid changing the props for ExpandedFundList on every render with a new reference to
 * a different actions object that is essential the same actions objects, just recreated with the same methods.
 *
 * @summary Modifies the actions object to add a selectFund implementation with IRA withholding modal functionality
 *
 * @type {Function}
 *
 */

const augmentSelectCollegeDistributeFundCreatorMemoized = strictMemoize(
  function augmentSelectCollegeDistributeFundCreator(
    actions,
    sellWithholdingWarned
  ) {
    return {
      ...actions,
      selectFund: (agreementID, fund) => {
        actions.selectSellFund(agreementID);
        // TODO: Add && !(!customerWebViewEnabled && isSalesforceRep)
        if (!sellWithholdingWarned && isRetirementFund(fund)) {
          actions.openModal(IRA_WITHHOLDING_WARNING_MODAL, { agreementID });
        }
      },
    };
  }
);

const CollegeDistributeFundList = ({
  actions,
  currentStep,
  deliveryMethod,
  hasAPP,
  hasNotInheritedSEPOrSimpleIRA,
  hiddenAccountsNumber,
  isQuickSellWithoutZeroBalance,
  marketVolatility,
  personalData,
  quickSell: { showAllSellFunds },
  quickSellAgreementID,
  selectedBank,
  sellWithholdingWarned,
  transactionLimit,
  userAddress,
  userFunds,
  userFundList,
  userStateWithholdingData,
  validatedSellFundsList,
}) => (
  <StepExpanded>
    <ExpandedFundList
      IRALimit={{ data: { currentYearRemainingContributions: null } }}
      actions={augmentSelectCollegeDistributeFundCreatorMemoized(
        actions,
        sellWithholdingWarned
      )}
      availableFunds={validatedSellFundsList}
      currentStep={currentStep}
      currentYearInclusiveRemainingContributions={null}
      deliveryMethod={deliveryMethod}
      fundEditor={() => CollegeDistributeFundEditor}
      fundGroupHeader={CollegeContributeFundGroupHeader}
      hasAPP={hasAPP}
      hasNotInheritedSEPOrSimpleIRA={hasNotInheritedSEPOrSimpleIRA}
      hiddenAccountsNumber={hiddenAccountsNumber}
      isQuickSellWithoutZeroBalance={isQuickSellWithoutZeroBalance}
      marketVolatility={marketVolatility}
      numberOfFunds={userFundList.length}
      personalData={personalData}
      priorYearInclusiveRemainingContributions={null}
      quickBuy={{}}
      quickSellAgreementID={quickSellAgreementID}
      selectedBank={selectedBank}
      setShowAllSellFunds={actions.setShowAllSellFunds}
      shoppingCart={{}}
      showAllSellFunds={showAllSellFunds}
      transactionLimit={transactionLimit}
      userAddress={userAddress}
      userFunds={userFunds}
      userStateWithholdingData={userStateWithholdingData}
    />
  </StepExpanded>
);

const mapStateToProps = (state) => {
  const { userFunds, quickSell } = state;

  return {
    currentStep: getProgressCurrentStep(state),
    deliveryMethod: getDeliveryMethod(state),
    hasAPP: getHasAPP(state),
    hasNotInheritedSEPOrSimpleIRA: getHasNotInheritedSEPOrSimpleIRA(state),
    hiddenAccountsNumber: getHiddenAccountNumber(state),
    isQuickSellWithoutZeroBalance: getIsQuickSellWithoutZeroBalance(state),
    marketVolatility: getMarketVolatility(state),
    personalData: getPersonalInformationData(state),
    quickSellAgreementID: getQuickSellAgreementID(state),
    selectedBank: getSelectedBank(state),
    sellWithholdingWarned: getSellWithholdingWarned(state),
    transactionLimit: getTransactionLimit(state),
    userAddress: getUserAddress(state),
    quickSell,
    userFunds,
    userFundList: getUserFundList(state),
    validatedSellFundsList: getCollegeSellFundsList(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      // We rename some sell methods so shared components can use buy or sell methods interchangeably
      deselectFund: deselectSellFund,
      openModal,
      selectSellFund,
      setProgressFlag,
      setShowAllFundsStep,
      updateSellFund,
      updateSellFundAmount,
      updateSellFundDistributionDisclaimer,
      updateSellFundDistributionPayee,
      updateSellFundDistributionReason,
      updateSellFundUnit,
      setShowAllSellFunds,
    },
    dispatch
  ),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  CollegeDistributeFundList
);
